/**
 * Display-safe error normalization for the shared Axios client
 * (Requirements 2.3, 10.1-10.4).
 *
 * Both exported functions are pure: no console, no network, no timers. The only
 * ambient value is `Date.now()`, used to resolve an HTTP-date `Retry-After`, and it
 * is injectable so the module stays deterministic under test.
 *
 * The guiding rule is that user-facing text is built from an allow-list, never from
 * whatever the backend happened to put in the payload. A sanitized backend error is
 * expected to carry `message` and `reference_id`; anything else in the body —
 * `sql`, `query`, `traceback`, `exception`, `stack` — is deliberately unreachable
 * from the returned `message` (Req 10.2). `raw` keeps the original error for
 * logging, and `formatErrorMessage` never touches it.
 */

import { resolveRetryDelayMs } from "./httpRetryPolicy";

/** Error categories surfaced to the UI. */
export const ERROR_KIND = {
  ACCESS_DENIED: "ACCESS_DENIED",
  UNAUTHORIZED: "UNAUTHORIZED",
  RATE_LIMITED: "RATE_LIMITED",
  VALIDATION: "VALIDATION",
  SERVER: "SERVER",
  NETWORK: "NETWORK",
  DOCS_BLOCKED: "DOCS_BLOCKED",
  UNKNOWN: "UNKNOWN",
};

/**
 * Exact copy required by Req 2.3. Note the em dash: this string is asserted
 * verbatim, so it is exported rather than inlined.
 */
export const ACCESS_DENIED_MESSAGE =
  "Access Denied — you do not have permission for this action.";

/** Generic per-kind fallbacks used whenever no allow-listed message exists. */
export const GENERIC_MESSAGES = {
  [ERROR_KIND.ACCESS_DENIED]: ACCESS_DENIED_MESSAGE,
  [ERROR_KIND.UNAUTHORIZED]: "Your session has expired. Please sign in again.",
  [ERROR_KIND.RATE_LIMITED]: "Too many requests. Please try again shortly.",
  [ERROR_KIND.VALIDATION]:
    "The request could not be completed. Please check your input and try again.",
  [ERROR_KIND.SERVER]: "Something went wrong on the server. Please try again.",
  [ERROR_KIND.NETWORK]:
    "Unable to reach the server. Check your connection and try again.",
  [ERROR_KIND.DOCS_BLOCKED]:
    "API documentation endpoints are disabled in this environment.",
  [ERROR_KIND.UNKNOWN]: "Something went wrong. Please try again.",
};

/** Label used for the reference suffix (Req 10.3). */
const REFERENCE_LABEL = "Reference";

/**
 * Payload keys that may carry raw database or runtime diagnostics. They are never
 * read as a message source; the list exists only so an allow-listed field that
 * duplicates one of these values can be rejected (Req 10.2).
 */
const RAW_DETAIL_KEYS = ["sql", "query", "traceback", "exception", "stack"];

/** Trimmed non-empty string, or null. */
function readString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Plain-ish object test: excludes null and arrays. */
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Case-insensitive header lookup across plain objects and AxiosHeaders. */
function readHeader(headers, name) {
  if (headers == null) return null;

  if (typeof headers.get === "function") {
    try {
      const viaGetter = headers.get(name);
      if (viaGetter != null) return viaGetter;
    } catch {
      /* fall through to the object scan */
    }
  }

  if (!isRecord(headers)) return null;

  const target = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === target) return headers[key];
  }
  return null;
}

/** The response body, when the error carries one. */
function readData(error) {
  if (!isRecord(error)) return null;
  const response = error.response;
  if (!isRecord(response)) return null;
  return response.data;
}

/** HTTP status, or null for transport-level failures. */
function readStatus(error) {
  if (!isRecord(error)) return null;

  const fromResponse = isRecord(error.response) ? error.response.status : undefined;
  const status = fromResponse === undefined ? error.status : fromResponse;

  return typeof status === "number" && Number.isFinite(status) ? status : null;
}

/** True for errors raised by the production documentation guard (Req 12). */
function isDocsBlocked(error) {
  if (!isRecord(error)) return false;
  return (
    error.code === ERROR_KIND.DOCS_BLOCKED ||
    error.kind === ERROR_KIND.DOCS_BLOCKED ||
    error.name === "DocsEndpointBlockedError"
  );
}

/** True when the failure never produced a response. */
function isNetworkFailure(error) {
  if (!isRecord(error)) return false;
  if (isRecord(error.response)) return false;

  if (error.isAxiosError === true) return true;
  if (error.request != null) return true;

  const code = typeof error.code === "string" ? error.code.toUpperCase() : "";
  return (
    code === "ECONNABORTED" ||
    code === "ECONNREFUSED" ||
    code === "ETIMEDOUT" ||
    code === "ERR_NETWORK"
  );
}

/**
 * Maps an error onto a display kind.
 *
 * Status drives the decision, matching `httpRetryPolicy`: 401 → UNAUTHORIZED,
 * 403 → ACCESS_DENIED, 429 → RATE_LIMITED, remaining 4xx → VALIDATION,
 * 5xx → SERVER. A failure with no response is NETWORK; anything else is UNKNOWN.
 *
 * @param {unknown} error
 * @returns {string} one of `ERROR_KIND`
 */
export function classifyErrorKind(error) {
  if (isDocsBlocked(error)) return ERROR_KIND.DOCS_BLOCKED;

  const status = readStatus(error);

  if (status === 401) return ERROR_KIND.UNAUTHORIZED;
  if (status === 403) return ERROR_KIND.ACCESS_DENIED;
  if (status === 429) return ERROR_KIND.RATE_LIMITED;
  if (status !== null && status >= 400 && status < 500) return ERROR_KIND.VALIDATION;
  if (status !== null && status >= 500 && status < 600) return ERROR_KIND.SERVER;

  if (status === null && isNetworkFailure(error)) return ERROR_KIND.NETWORK;

  return ERROR_KIND.UNKNOWN;
}

/**
 * Reads the reference ID from the supported nesting locations, in order:
 * `data.reference_id`, `data.detail.reference_id`, `data.error.reference_id`
 * (FastAPI wraps handled errors in `detail`). Absent, empty, or non-scalar → null
 * (Req 10.4).
 *
 * @param {unknown} error
 * @returns {string|null}
 */
export function readReferenceId(error) {
  const data = readData(error);
  if (!isRecord(data)) return null;

  const candidates = [
    data.reference_id,
    isRecord(data.detail) ? data.detail.reference_id : undefined,
    isRecord(data.error) ? data.error.reference_id : undefined,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) {
      return String(candidate);
    }
    const asString = readString(candidate);
    if (asString) return asString;
  }

  return null;
}

/** Raw diagnostic values present anywhere in the allow-listed containers. */
function collectRawDetailValues(data) {
  const values = [];
  const containers = [data, isRecord(data.detail) ? data.detail : null, isRecord(data.error) ? data.error : null];

  for (const container of containers) {
    if (!isRecord(container)) continue;
    for (const key of RAW_DETAIL_KEYS) {
      const value = readString(container[key]);
      if (value) values.push(value);
    }
  }

  return values;
}

/**
 * Picks the message from the allow-list only.
 *
 * Order: `data.message`, `data.detail` when it is a string, `data.detail.message`,
 * `data.error.message`. Nothing else is consulted — not `error.message`, not any
 * other payload key — so raw database text cannot reach the UI even when the
 * backend forgets to sanitize. As a second line of defence, an allow-listed value
 * that merely repeats a raw diagnostic field is rejected too (Req 10.2).
 *
 * @param {unknown} error
 * @returns {string|null} the sanitized message, or null when none is usable
 */
export function readAllowListedMessage(error) {
  const data = readData(error);
  if (!isRecord(data)) return null;

  const candidates = [
    data.message,
    typeof data.detail === "string" ? data.detail : undefined,
    isRecord(data.detail) ? data.detail.message : undefined,
    isRecord(data.error) ? data.error.message : undefined,
  ];

  const forbidden = collectRawDetailValues(data);

  for (const candidate of candidates) {
    const value = readString(candidate);
    if (!value) continue;
    if (forbidden.some((raw) => value === raw || value.includes(raw))) continue;
    return value;
  }

  return null;
}

/**
 * Milliseconds to wait after a 429, resolved from `Retry-After`; null otherwise.
 * The parsing itself is delegated to `httpRetryPolicy` so the interceptor and the
 * descriptor can never disagree about the delay.
 */
function readRetryAfterMs(error, kind, now) {
  if (kind !== ERROR_KIND.RATE_LIMITED) return null;
  const headers = isRecord(error) && isRecord(error.response) ? error.response.headers : null;
  return resolveRetryDelayMs(readHeader(headers, "retry-after"), now);
}

/**
 * Turns any thrown value into a display-safe descriptor.
 *
 * `message` is either an allow-listed backend string or a generic per-kind
 * fallback, and is always a non-empty string. `ACCESS_DENIED` is special-cased to
 * the exact Req 2.3 copy, so a 403 body cannot override it.
 *
 * @param {unknown} error the Axios error, or anything else that was thrown
 * @param {{ now?: number }} [options] `now` is used only for HTTP-date `Retry-After`
 * @returns {{ kind: string, status: number|null, message: string,
 *             referenceId: string|null, retryAfterMs: number|null, raw: unknown }}
 */
export function normalizeApiError(error, { now } = {}) {
  const kind = classifyErrorKind(error);
  const status = readStatus(error);
  const referenceId = readReferenceId(error);

  const message =
    kind === ERROR_KIND.ACCESS_DENIED
      ? ACCESS_DENIED_MESSAGE
      : readAllowListedMessage(error) ?? GENERIC_MESSAGES[kind];

  return {
    kind,
    status,
    message,
    referenceId,
    retryAfterMs: readRetryAfterMs(error, kind, now === undefined ? Date.now() : now),
    raw: error,
  };
}

/**
 * Builds the single string shown to the user.
 *
 * Only `message` and `referenceId` are read; `raw` is never consulted, which is
 * what keeps raw payload content out of the display path. The reference suffix is
 * appended only when a reference exists (Req 10.1, 10.3, 10.4).
 *
 * @param {{ kind?: string, message?: string, referenceId?: string|number|null }} [descriptor]
 * @returns {string}
 */
export function formatErrorMessage(descriptor) {
  const source = isRecord(descriptor) ? descriptor : {};

  const fallback =
    GENERIC_MESSAGES[source.kind] === undefined
      ? GENERIC_MESSAGES[ERROR_KIND.UNKNOWN]
      : GENERIC_MESSAGES[source.kind];

  const message = readString(source.message) ?? fallback;

  const rawReference = source.referenceId;
  const reference =
    typeof rawReference === "number" && Number.isFinite(rawReference)
      ? String(rawReference)
      : readString(rawReference);

  return reference ? `${message} (${REFERENCE_LABEL}: ${reference})` : message;
}
